<template>
  <div v-if="isVisibleOverlay && isUsingLanguageDetection" class="overlay"></div>
  <div
    class="language-content"
    :class="{
      'selected-at-top': selectedAtTop && !showAllInList,
      disabled: isVisibleOverlay && isUsingLanguageDetection,
      'floater-active': isVisibleFloater && isUsingLanguageDetection,
    }"
  >
    <ul ref="languageListRef" :class="{ 'language-list': true }">
      <li
        v-for="language in orderedLanguages"
        :key="language.code"
        :ref="
          (el) => {
            if (language.isLanguageDetected) refLanguageDetected = el as HTMLElement;
          }
        "
        :class="{ 'language-detected': language.isLanguageDetected }"
        style="position: relative"
        @click="$emit('select', language)"
      >
        <div class="language-item" :class="{ left: alignLeft }">
          <button class="m-btn button-white button-full-width">
            <!-- 체크박스: showCheckbox가 true일 때만 표시 -->
            <input
              v-if="showCheckbox"
              type="checkbox"
              :value="language"
              :checked="checkedLanguages.some(({ code }) => code === language.code)"
              @click.stop="emit('update:checkedLanguages', language)"
            />
            {{ language.name }} - {{ t(`label_language_${language.code}`) }}
          </button>
        </div>
      </li>
    </ul>
    <div v-if="!showAllInList" class="divider" />
    <p v-if="!showAllInList && languageSelected" class="small selected-item">
      {{ languageSelected.name }} - {{ t(`label_language_${languageSelected.code}`) }}
    </p>
  </div>

  <Teleport to="body">
    <div
      v-if="isVisibleFloater && isUsingLanguageDetection"
      class="floater-wrapper"
      :style="{
        bottom: floaterBottom,
        '--close-translate-x': props.closeTranslateX,
        '--close-translate-x-mobile': props.closeTranslateXMobile,
        '--floater-margin': props.floaterMargin,
      }"
    >
      <div class="floater-inr">
        <div class="floater up">
          <p :style="styleFloater" v-html="languageDetectedMessageBackup" />
          <p :style="styleFloater" v-html="languageDetectedMessage" />
        </div>

        <div class="floater close">
          <IconClose class="icon-close" @click.stop="onCloseDetection" />
        </div>
      </div>
    </div>
  </Teleport>
</template>

<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';

import { storeToRefs } from 'pinia';

import IconClose from '@/components/icons/IconClose.vue';
import { useAudioStore } from '@/stores/audio';
import { useToastStore } from '@/stores/toast.js';
import { Language } from '@/types/common';

const props = withDefaults(
  defineProps<{
    isUsingLanguageDetection?: boolean;
    show?: boolean;
    reverseOrder?: boolean;
    selectedAtTop?: boolean;
    showAllInList?: boolean;
    floaterBottom?: string;
    detectedGradient?: string;
    closeTranslateX?: string;
    closeTranslateXMobile?: string;
    floaterMargin?: string;
    showCheckbox?: boolean;
    alignLeft?: boolean;
    checkedLanguages?: any[];
    languages: Language[];
  }>(),
  {
    isUsingLanguageDetection: false,
    show: false,
    reverseOrder: false,
    selectedAtTop: false,
    showAllInList: false,
    floaterBottom: '6.5rem',
    detectedGradient: 'var(--gradient-primary)',
    closeTranslateX: '0rem',
    closeTranslateXMobile: '0rem',
    floaterMargin: '0rem',
    showCheckbox: false,
    alignLeft: false,
    languages: () => [],
    checkedLanguages: () => [],
  }
);

const audioStore = useAudioStore();
const toastStore = useToastStore();

const { t, messages } = useI18n();
const { languageSelected, languageDetected } = storeToRefs(audioStore);
const { hintBoxHtml, showHintBox } = storeToRefs(toastStore);

const emit = defineEmits(['select', 'close', 'update:checkedLanguages']);

const languageListRef = ref<HTMLElement>();
const refLanguageDetected = ref<HTMLElement>();
const isVisibleOverlay = ref(false);
const isVisibleFloater = ref(false);
const styleFloater = ref({});

// 언어 감지 안내문 (선택된 언어로)
const languageDetectedMessage = computed(() => {
  const langCodeSelected = languageSelected.value?.code || 'ko'; // 선택된 언어 코드
  const langCodeDetected = languageDetected.value?.languageCode; // 감지된 언어 코드
  const messagesLangSelected = messages.value?.[langCodeSelected]; // 선택된 언어의 메시지
  const nameLanguage = messagesLangSelected[`language_${langCodeDetected}`]; // 언어의 이름
  return messagesLangSelected['detected_language_message']?.replace('{lang}', nameLanguage);
});

// 언어 감지 안내문 (감지된 언어로)
const languageDetectedMessageBackup = computed(() => {
  const langCode = languageDetected.value?.languageCode || 'ko';
  const nameLanguage = messages.value[langCode]['language_meta'];
  const rawTemplate = messages.value?.[langCode]?.detected_language_message || '';

  return rawTemplate.replace('{lang}', nameLanguage);
});

// 리스트 reverse, 선택된 언어는 제외
const orderedLanguages = computed(() => {
  const selectedCode = languageSelected.value?.code;
  const detectedCode = languageDetected.value?.languageCode;

  // 감지된 언어와 선택된 언어가 동일하면 감지된 언어는 따로 처리하지 않음
  const shouldAddDetectedSeparately = detectedCode && detectedCode !== selectedCode;

  // 선택된 언어와 (필요한 경우) 감지된 언어를 제외
  const excludedCodes = [selectedCode];
  if (shouldAddDetectedSeparately) excludedCodes.push(detectedCode);

  const baseArray = props.showAllInList
    ? props.languages
    : props.languages.filter((lang) => !excludedCodes.includes(lang.code));

  const languagesOrdered = baseArray.map((lang) => ({
    ...lang,
    isLanguageDetected: false,
  }));

  if (props.reverseOrder) languagesOrdered.reverse();

  // 감지된 언어를 맨 뒤에 추가
  if (shouldAddDetectedSeparately) {
    const detectedItem = props.languages.find((lang) => lang.code === detectedCode);
    if (detectedItem) {
      languagesOrdered.push({ ...detectedItem, isLanguageDetected: true });
    }
  }

  return languagesOrdered;
});

const onCloseDetection = () => emit('close');

const scrollToBottom = () => {
  if (languageListRef.value) {
    languageListRef.value.scrollTop = languageListRef.value.scrollHeight;
  }
};

watch(
  () => props.show,
  (newVal) => {
    // show가 true로 변경될 때 스크롤을 하단으로 이동시킵니다.
    if (newVal) scrollToBottom();
  }
);

// 감지된 언어와 선택된 언어가 다를 경우
watch(
  () => languageDetected.value,
  async (newVal) => {
    if (!newVal || !props.isUsingLanguageDetection) return;
    if (languageSelected.value.code !== newVal?.languageCode) {
      if (newVal.step === 2) {
        showHintBox.value = '';
        hintBoxHtml.value = null;
        isVisibleOverlay.value = true;
        isVisibleFloater.value = true;
      } else {
        isVisibleOverlay.value = false;
        isVisibleFloater.value = false;
        emit('close');
      }
    }
  },
  { immediate: true }
);

// 언어 감지 2단계 안내문구 발생 시 자동 위치조정
watch(isVisibleFloater, (visible) => {
  if (visible && refLanguageDetected.value) {
    const rect = refLanguageDetected.value.getBoundingClientRect();
    const top = rect.top + window.scrollY;
    const left = rect.left + window.scrollX;

    styleFloater.value = {
      top: `${top}px`,
      left: `${left}px`,
    };
  }
});

onMounted(() => {
  scrollToBottom();
});
</script>

<style scoped lang="scss">
.overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 83px;
  width: 100dvw;
  background-color: var(--language-detection-overlay);
  backdrop-filter: $blur-light;
  -webkit-backdrop-filter: blur(4px);
  z-index: 10;
  pointer-events: none;
}

.language-content {
  min-width: 5.8125rem;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  border-radius: 8px;
  z-index: 1000;
  pointer-events: auto;
}

.language-content.disabled {
  pointer-events: none;
}

.language-content.disabled .language-detected {
  pointer-events: auto;
}

.selected-at-top {
  flex-direction: column-reverse;
}

.language-list {
  width: 100%;
}

.selected-item {
  padding: 0.25rem 0.5rem;
  width: 100%;
  text-align: center;
}

.divider {
  margin: 0.5rem 0;
}

.language-detected {
  position: relative;
  background-color: transparent;
  z-index: 1002;
  padding: 0.5rem;
  border-radius: 8px;
}

.language-detected::before {
  content: '';
  position: absolute;
  inset: 0;
  padding: 2px;
  border-radius: 8px;
  background: v-bind(detectedGradient);
  -webkit-mask:
    linear-gradient(#fff 0 0) content-box,
    linear-gradient(#fff 0 0);
  -webkit-mask-composite: destination-out;
  mask-composite: exclude;
  pointer-events: none;
  z-index: 1;
}

.language-detected-memo {
  width: 100%;
  display: flex;
  fill: var(--color-red);
  color: var(--color-red);
}

.floater-wrapper {
  position: fixed;
  z-index: 1005;
  pointer-events: none;
  bottom: 0 !important;
  width: calc(100dvw - 240px);
  height: fit-content;
  left: 240px;
  padding-bottom: 1rem;
  .floater-inr {
    position: absolute;
    max-width: 320px;
    width: 100%;
    bottom: 160px;
    left: calc(50% - 96px);
    transform: translateX(-50%);
    padding: 1.25rem;
    .floater {
      display: flex;
      flex-direction: column;
      text-align: center;
      gap: 0.75rem;
      align-items: flex-start;
      text-align: left;
      &.close {
        margin-top: 1rem;
        flex-direction: row;
        cursor: pointer;
      }
    }
  }
}

:root:has(.container-selfservice) {
  .floater-wrapper {
    .floater-inr {
      left: calc(50% + 29px); // 사이드바 있을 때
    }
  }
}

@include respond-to(tablet) {
  :root:has(.container-selfservice) {
    .floater-wrapper {
      width: calc(100dvw - 72px);
      left: 72px;
    }
  }
}
@media (max-width: 840px) {
  :root:has(.container-selfservice) {
    .floater-wrapper {
      .floater-inr {
        left: calc(50% + 92px);
      }
    }
  }
}

@include respond-to(mobile-landscape) {
  :root:has(.container-selfservice) {
    .floater-wrapper {
      width: 100dvw;
      left: 0;
    }
  }
  .floater-wrapper {
    width: 100dvw;
    left: 0;
    .floater-inr {
      left: calc(50% + 92px);
    }
  }
}
@include respond-to(mobile-portrait) {
  :root:has(.container-selfservice) {
    .floater-wrapper {
      .floater-inr {
        left: auto;
        right: 16px;
        transform: translateX(0);
        .floater {
          text-align: right;
          justify-content: flex-end;
        }
      }
    }
  }
  .floater-wrapper {
    .floater-inr {
      left: auto;
      right: 16px;
      transform: translateX(0);
      .floater {
        text-align: right;
        justify-content: flex-end;
      }
    }
  }
}

.icon-close {
  pointer-events: auto;
  width: 2rem;
  height: 2rem;
  padding: 0.25rem;
  border-radius: 50%;
  background-color: var(--color-gray-30);
}

.original {
  color: var(--color-gray-400);
}

.language-item {
  display: flex;
  gap: 1rem;
  justify-content: center;
  align-items: center;

  &.left {
    justify-content: flex-start;
  }
}

input[type='checkbox'] {
  top: unset;
}
</style>

<style>
.detected-language {
  font-weight: var(--font-weight-semibold);
  font-size: var(--font-size-h4);
  background: v-bind(detectedGradient);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
}

.original .detected-language {
  font-size: var(--font-size-p);
  background: none;
  -webkit-background-clip: unset;
  -webkit-text-fill-color: unset;
}
</style>
